import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, AlertTriangle, Loader } from 'lucide-react';
import ApiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const EnvironmentDeleteButton = ({ environmentName }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { isSuperUser } = useAuth();

    if (!isSuperUser) {
        return null;
    }

    const openModal = () => {
        setIsModalOpen(true);
        setError(null);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        setError(null);

        try {
            await ApiService.deleteEnvironment(environmentName);

            setIsModalOpen(false);
            navigate('/moneysaver', {
                state: {
                    notification: {
                        type: 'success',
                        message: `Environment "${environmentName}" has been deleted successfully.`
                    }
                }
            });
        } catch (error) {
            console.error('Failed to delete environment:', error);
            setError('Failed to delete environment. Please try again.');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <button
                onClick={openModal}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md shadow-sm flex items-center"
                title="Delete Environment"
            >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Environment
            </button>

            {/* Confirmation Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-red-600">Delete Environment</h2>
                            <button
                                onClick={closeModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ×
                            </button>
                        </div>

                        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <AlertTriangle className="h-5 w-5 text-red-500" />
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700 font-bold">
                                        Warning: This action cannot be undone!
                                    </p>
                                    <p className="text-sm text-red-700 mt-2">
                                        Are you sure you want to delete the <span className="font-bold">{environmentName}</span> environment?
                                    </p>
                                    <p className="text-sm text-red-700 mt-2">
                                        Deleting this environment will remove all its configuration from the system.
                                        This will not delete the actual AWS resources in the account.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {error && (
                            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                                <div className="flex">
                                    <div className="ml-3">
                                        <p className="text-sm text-red-700">{error}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={closeModal}
                                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={isDeleting}
                                className={`px-4 py-2 text-white rounded-md shadow-sm flex items-center ${
                                    isDeleting ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
                                }`}
                            >
                                {isDeleting ? (
                                    <>
                                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Delete Environment
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default EnvironmentDeleteButton;